import React, { useState, useMemo, useCallback } from 'react';
import {
  X,
  Search,
  Check,
  ArrowRight,
  ArrowLeft,
  Plus,
  RotateCcw,
  Columns3,
  Server,
  Zap,
} from 'lucide-react';
import { useEditorStore, RackNode } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import {
  groupRacksByTenant,
  getTenantMeta,
  filterRacks,
  calculateAllocationStats,
  TenantGroupMeta,
} from '../../utils/rackAllocation.logic';
import {
  ALL_RACK_STATUSES,
  ALL_RACK_ROLES,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
  RackLifecycleStatus,
  RackRole,
} from '../../utils/rackLifecycle.logic';

export const RackAllocationModal: React.FC = () => {
  const { racks, showRackAllocation, setShowRackAllocation, applyRackAllocation } = useEditorStore(
    useShallow(state => ({
      racks: state.racks,
      showRackAllocation: state.showRackAllocation,
      setShowRackAllocation: state.setShowRackAllocation,
      applyRackAllocation: state.applyRackAllocation,
    }))
  );

  // Local working copy of tenant assignments: { [rackId]: tenantKey }
  const [localAllocations, setLocalAllocations] = useState<Record<string, string>>({});
  // Selected rack IDs for batch transfer
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Search query
  const [search, setSearch] = useState('');
  // Custom groups added by user in this session
  const [customGroups, setCustomGroups] = useState<TenantGroupMeta[]>([]);
  // Cascade tenant to child equipment upon transfer/apply
  const [cascadeToEquipment, setCascadeToEquipment] = useState(true);
  // Input for adding a new group
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Synchronize initial state when modal opens
  React.useEffect(() => {
    if (showRackAllocation) {
      const initial: Record<string, string> = {};
      Object.values(racks).forEach(r => {
        initial[r.id] = (r.tenant || 'mine').trim();
      });
      setLocalAllocations(initial);
      setSelectedIds(new Set());
      setSearch('');
      setIsAddingGroup(false);
      setNewGroupName('');
    }
  }, [showRackAllocation, racks]);

  // Handle ESC key to close modal
  React.useEffect(() => {
    if (!showRackAllocation) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowRackAllocation(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showRackAllocation, setShowRackAllocation]);

  // Merge working allocations with rack definitions
  const effectiveRacks: RackNode[] = useMemo(() => {
    return Object.values(racks).map(r => ({
      ...r,
      tenant: localAllocations[r.id] ?? r.tenant ?? 'mine',
    }));
  }, [racks, localAllocations]);

  // All active group keys
  const allGroupKeys = useMemo(() => {
    const keys = new Set<string>(['mine', 'other', 'reserved']);
    customGroups.forEach(g => keys.add(g.key));
    Object.values(localAllocations).forEach(t => {
      if (t) keys.add(t);
    });
    return Array.from(keys);
  }, [customGroups, localAllocations]);

  // Grouped racks dictionary
  const groupedRacks = useMemo(() => {
    return groupRacksByTenant(effectiveRacks, allGroupKeys);
  }, [effectiveRacks, allGroupKeys]);

  // Stats calculation
  const stats = useMemo(() => {
    return calculateAllocationStats(effectiveRacks);
  }, [effectiveRacks]);

  // Calculate count of modified racks compared to store
  const changesCount = useMemo(() => {
    let count = 0;
    Object.entries(localAllocations).forEach(([id, tenant]) => {
      const original = (racks[id]?.tenant || 'mine').trim();
      if (original !== tenant) count++;
    });
    return count;
  }, [localAllocations, racks]);

  // Toggle single rack selection
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Select all in a specific group
  const handleSelectAllInGroup = useCallback(
    (groupKey: string) => {
      const groupItems = groupedRacks[groupKey] || [];
      setSelectedIds(prev => {
        const next = new Set(prev);
        const allAlreadySelected = groupItems.every(r => next.has(r.id));
        if (allAlreadySelected) {
          groupItems.forEach(r => next.delete(r.id));
        } else {
          groupItems.forEach(r => next.add(r.id));
        }
        return next;
      });
    },
    [groupedRacks]
  );

  // Transfer selected racks to target tenant
  const handleTransferSelected = useCallback(
    (targetTenant: string) => {
      if (selectedIds.size === 0) return;
      setLocalAllocations(prev => {
        const next = { ...prev };
        selectedIds.forEach(id => {
          next[id] = targetTenant;
        });
        return next;
      });
      // Keep selection so user sees where they moved
    },
    [selectedIds]
  );

  // Transfer single rack directly to target
  const handleTransferSingle = useCallback((rackId: string, targetTenant: string) => {
    setLocalAllocations(prev => ({
      ...prev,
      [rackId]: targetTenant,
    }));
  }, []);

  // Batch update lifecycle status for selected racks
  const handleBatchStatus = useCallback((status: RackLifecycleStatus) => {
    if (selectedIds.size === 0) return;
    useEditorStore.getState().batchUpdateRacks(Array.from(selectedIds), { status });
  }, [selectedIds]);

  // Batch update functional role for selected racks
  const handleBatchRole = useCallback((role: RackRole) => {
    if (selectedIds.size === 0) return;
    useEditorStore.getState().batchUpdateRacks(Array.from(selectedIds), { role });
  }, [selectedIds]);

  // Add new custom group
  const handleAddGroup = () => {
    const trimmed = newGroupName.trim();
    if (!trimmed) return;
    if (!allGroupKeys.includes(trimmed)) {
      const meta = getTenantMeta(trimmed);
      setCustomGroups(prev => [...prev, meta]);
    }
    setNewGroupName('');
    setIsAddingGroup(false);
  };

  // Reset allocations to store state
  const handleReset = () => {
    const resetState: Record<string, string> = {};
    Object.values(racks).forEach(r => {
      resetState[r.id] = (r.tenant || 'mine').trim();
    });
    setLocalAllocations(resetState);
    setSelectedIds(new Set());
  };

  // Commit changes to editorStore
  const handleApply = () => {
    applyRackAllocation(localAllocations, cascadeToEquipment);
  };

  if (!showRackAllocation) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(5, 8, 15, 0.82)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) setShowRackAllocation(false);
      }}
    >
      <div
        style={{
          width: '95vw',
          maxWidth: 1380,
          height: '88vh',
          maxHeight: 900,
          background: 'var(--bg-panel, #0d1117)',
          border: '1px solid var(--border-color, #21262d)',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          color: 'var(--text-primary, #f3f4f6)',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color, #21262d)',
            background: 'var(--bg-deep, #060a0f)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: 'rgba(168, 85, 247, 0.15)',
                border: '1px solid rgba(168, 85, 247, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#c084fc',
              }}
            >
              <Columns3 size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
                Матрица распределения стоек (Transfer Matrix)
              </h2>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', marginTop: 2 }}>
                Быстрое групповое перемещение шкафов по владельцам, арендаторам и проектам
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--bg-card, #161b22)',
                border: '1px solid var(--border-color, #30363d)',
                borderRadius: 6,
                padding: '4px 10px',
                width: 220,
              }}
            >
              <Search size={13} style={{ color: 'var(--text-muted, #8b949e)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск по метке, ряду, статусу, роли..."
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'inherit',
                  fontSize: 12,
                  width: '100%',
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  style={{ background: 'transparent', border: 'none', color: '#8b949e', cursor: 'pointer', padding: 0 }}
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Close */}
            <button
              onClick={() => setShowRackAllocation(false)}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-color, #30363d)',
                borderRadius: 6,
                color: 'var(--text-muted, #8b949e)',
                padding: '5px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Закрыть"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Global Stats Bar & Quick Move Toolbar */}
        <div
          style={{
            padding: '10px 20px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderBottom: '1px solid var(--border-color, #21262d)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {/* Summary Chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ color: 'var(--text-muted, #9ca3af)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Server size={13} />
              Всего стоек: <strong style={{ color: '#fff' }}>{stats.totalRacks}</strong>
            </span>
            <span style={{ color: 'var(--text-muted, #9ca3af)' }}>
              IT-шкафов: <strong style={{ color: '#60a5fa' }}>{stats.itRacks}</strong>
            </span>
            <span style={{ color: 'var(--text-muted, #9ca3af)' }}>
              Инженерных: <strong style={{ color: '#a855f7' }}>{stats.facilityRacks}</strong>
            </span>
            {changesCount > 0 && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: 'rgba(245, 158, 11, 0.15)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  color: '#fbbf24',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Изменено: {changesCount}
              </span>
            )}
          </div>

          {/* Quick Transfer Actions for Selected Racks */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: 11 }}>
              Выделено: <strong style={{ color: selectedIds.size > 0 ? '#60a5fa' : '#8b949e' }}>{selectedIds.size}</strong>
            </span>

            {selectedIds.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#8b949e' }}>Переместить в:</span>
                {allGroupKeys.map(key => {
                  const meta = getTenantMeta(key, customGroups);
                  return (
                    <button
                      key={key}
                      onClick={() => handleTransferSelected(key)}
                      style={{
                        padding: '3px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: meta.badgeBg,
                        border: `1px solid ${meta.badgeBorder}`,
                        color: meta.color,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <ArrowRight size={11} /> {meta.name}
                    </button>
                  );
                })}

                {/* Batch Status Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid #30363d' }}>
                  <span style={{ fontSize: 11, color: '#8b949e' }}>Статус:</span>
                  {ALL_RACK_STATUSES.map(st => {
                    const meta = RACK_STATUS_METAS[st];
                    return (
                      <button
                        key={st}
                        onClick={() => handleBatchStatus(st)}
                        style={{
                          padding: '3px 7px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: meta.badgeBg,
                          border: `1px solid ${meta.badgeBorder}`,
                          color: meta.color,
                        }}
                        title={`Установить статус «${meta.labelRu}» (${selectedIds.size} стоек)`}
                      >
                        {meta.labelRu}
                      </button>
                    );
                  })}
                </div>

                {/* Batch Role Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6, paddingLeft: 6, borderLeft: '1px solid #30363d' }}>
                  <span style={{ fontSize: 11, color: '#8b949e' }}>Роль:</span>
                  {ALL_RACK_ROLES.map(rl => {
                    const meta = RACK_ROLE_METAS[rl];
                    return (
                      <button
                        key={rl}
                        onClick={() => handleBatchRole(rl)}
                        style={{
                          padding: '3px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: meta.badgeBg,
                          border: `1px solid ${meta.badgeBorder}`,
                          color: meta.color,
                        }}
                        title={`Установить роль «${meta.labelRu}» (${selectedIds.size} стоек)`}
                      >
                        {meta.shortCode}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add Custom Group Button */}
            {!isAddingGroup ? (
              <button
                onClick={() => setIsAddingGroup(true)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  background: 'var(--bg-card, #161b22)',
                  border: '1px solid var(--border-color, #30363d)',
                  color: 'var(--text-secondary, #9ca3af)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Plus size={12} /> Новая группа
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  autoFocus
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleAddGroup();
                    if (e.key === 'Escape') setIsAddingGroup(false);
                  }}
                  placeholder="Название группы/клиента..."
                  style={{
                    background: 'var(--bg-input, #0d1117)',
                    border: '1px solid var(--color-accent-blue, #3b82f6)',
                    borderRadius: 4,
                    color: '#fff',
                    padding: '3px 8px',
                    fontSize: 11,
                    outline: 'none',
                    width: 160,
                  }}
                />
                <button
                  onClick={handleAddGroup}
                  style={{
                    padding: '3px 7px',
                    borderRadius: 4,
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  OK
                </button>
                <button
                  onClick={() => setIsAddingGroup(false)}
                  style={{ background: 'transparent', border: 'none', color: '#8b949e', cursor: 'pointer', padding: 2 }}
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Multi-Column Transfer Matrix Canvas */}
        <div
          style={{
            flex: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '16px 20px',
            display: 'flex',
            gap: 16,
            background: 'var(--bg-deep, #060a0f)',
          }}
        >
          {allGroupKeys.map((groupKey, colIdx) => {
            const meta = getTenantMeta(groupKey, customGroups);
            const allItems = groupedRacks[groupKey] || [];
            const items = filterRacks(allItems, search);
            const isAllSelected = items.length > 0 && items.every(r => selectedIds.has(r.id));
            const groupPower = stats.powerByTenant[groupKey] || 0;

            const prevGroupKey = colIdx > 0 ? allGroupKeys[colIdx - 1] : null;
            const nextGroupKey = colIdx < allGroupKeys.length - 1 ? allGroupKeys[colIdx + 1] : null;

            return (
              <div
                key={groupKey}
                style={{
                  flex: '0 0 320px',
                  width: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'var(--bg-panel, #0d1117)',
                  border: `1px solid ${meta.badgeBorder}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                }}
              >
                {/* Column Header */}
                <div
                  style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid var(--border-color, #21262d)',
                    background: meta.badgeBg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: meta.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontWeight: 700, fontSize: 13, color: meta.color }}>
                        {meta.name}
                      </span>
                    </div>

                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 10,
                        background: 'rgba(0,0,0,0.4)',
                        color: meta.color,
                        border: `1px solid ${meta.badgeBorder}`,
                      }}
                    >
                      {items.length} {items.length !== allItems.length ? `из ${allItems.length}` : 'стоек'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted, #9ca3af)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Zap size={11} color={meta.color} /> {groupPower} кВт суммарно
                    </span>

                    <button
                      onClick={() => handleSelectAllInGroup(groupKey)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: meta.color,
                        fontSize: 10,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      {isAllSelected ? 'Снять всё' : 'Выбрать все'}
                    </button>
                  </div>
                </div>

                {/* Rack Cards List */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {items.length === 0 ? (
                    <div
                      style={{
                        padding: '30px 10px',
                        textAlign: 'center',
                        color: 'var(--text-muted, #6b7280)',
                        fontSize: 11,
                        border: '1px dashed var(--border-color, #21262d)',
                        borderRadius: 6,
                        margin: '10px 0',
                      }}
                    >
                      {search ? 'Нет совпадений' : 'В этой группе нет стоек'}
                    </div>
                  ) : (
                    items.map(rack => {
                      const isSel = selectedIds.has(rack.id);
                      const isModified = (racks[rack.id]?.tenant || 'mine').trim() !== (rack.tenant || 'mine').trim();

                      return (
                        <div
                          key={rack.id}
                          onClick={() => handleToggleSelect(rack.id)}
                          style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            background: isSel
                              ? 'rgba(59, 130, 246, 0.16)'
                              : 'var(--bg-card, #161b22)',
                            border: isSel
                              ? '1px solid var(--color-accent-blue, #3b82f6)'
                              : isModified
                              ? '1px solid rgba(245, 158, 11, 0.5)'
                              : '1px solid var(--border-color, #21262d)',
                            cursor: 'pointer',
                            transition: 'all 0.12s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            boxShadow: isSel ? '0 0 8px rgba(59, 130, 246, 0.25)' : 'none',
                          }}
                        >
                          {/* Rack Card Header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => {}} // Handled by card click
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                              />
                              <span style={{ fontWeight: 700, fontSize: 12, color: '#f3f4f6' }}>
                                {rack.label || rack.id}
                              </span>
                              {isModified && (
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} title="Изменено" />
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {rack.status && rack.status !== 'active' && (
                                <span
                                  style={{
                                    fontSize: 8,
                                    fontFamily: 'var(--font-data, monospace)',
                                    padding: '1px 4px',
                                    borderRadius: 3,
                                    background: rack.status === 'deprecated' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                                    color: rack.status === 'deprecated' ? '#f59e0b' : '#38bdf8',
                                    textTransform: 'uppercase',
                                    fontWeight: 700,
                                  }}
                                >
                                  {rack.status}
                                </span>
                              )}
                              <span
                                style={{
                                  fontSize: 9,
                                  fontFamily: 'var(--font-data, monospace)',
                                  padding: '1px 5px',
                                  borderRadius: 3,
                                  background: 'rgba(255,255,255,0.06)',
                                  color: 'var(--text-muted, #9ca3af)',
                                }}
                              >
                                {rack.role || rack.rackType || 'SRV'}
                              </span>
                            </div>
                          </div>

                          {/* Rack Card Meta Info */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted, #9ca3af)' }}>
                            <span>
                              {rack.rowId ? `Ряд: ${rack.rowId}` : 'Вне ряда'}
                            </span>
                            <span>
                              {rack.isFacility ? 'Инфраструктура' : `${rack.uCount || 42}U • ${rack.maxPowerKw || 5} кВт`}
                            </span>
                          </div>

                          {/* Action transfer arrows for single rack */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 4,
                              marginTop: 2,
                              paddingTop: 4,
                              borderTop: '1px solid rgba(255,255,255,0.05)',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            {prevGroupKey && (
                              <button
                                onClick={() => handleTransferSingle(rack.id, prevGroupKey)}
                                style={{
                                  padding: '2px 5px',
                                  background: 'rgba(255,255,255,0.05)',
                                  border: '1px solid var(--border-color, #30363d)',
                                  borderRadius: 3,
                                  color: 'var(--text-muted, #9ca3af)',
                                  cursor: 'pointer',
                                  fontSize: 9,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                                title={`Переместить в "${getTenantMeta(prevGroupKey, customGroups).name}"`}
                              >
                                <ArrowLeft size={10} /> Влево
                              </button>
                            )}

                            {nextGroupKey && (
                              <button
                                onClick={() => handleTransferSingle(rack.id, nextGroupKey)}
                                style={{
                                  padding: '2px 5px',
                                  background: 'rgba(255,255,255,0.05)',
                                  border: '1px solid var(--border-color, #30363d)',
                                  borderRadius: 3,
                                  color: 'var(--text-muted, #9ca3af)',
                                  cursor: 'pointer',
                                  fontSize: 9,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                                title={`Переместить в "${getTenantMeta(nextGroupKey, customGroups).name}"`}
                              >
                                Вправо <ArrowRight size={10} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--border-color, #21262d)',
            background: 'var(--bg-deep, #060a0f)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: 4,
                  color: 'var(--text-muted, #8b949e)',
                  padding: '5px 10px',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Снять выделение ({selectedIds.size})
              </button>
            )}

            {changesCount > 0 && (
              <button
                onClick={handleReset}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 4,
                  color: '#f87171',
                  padding: '5px 10px',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <RotateCcw size={12} /> Сбросить изменения
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--text-secondary, #c9d1d9)',
                cursor: 'pointer',
                userSelect: 'none',
                marginRight: 4,
              }}
            >
              <input
                type="checkbox"
                data-testid="allocation-cascade-checkbox"
                checked={cascadeToEquipment}
                onChange={e => setCascadeToEquipment(e.target.checked)}
                style={{ accentColor: '#3b82f6', cursor: 'pointer' }}
              />
              <span>Применить владельца ко всему установленному оборудованию</span>
            </label>

            <button
              onClick={() => setShowRackAllocation(false)}
              style={{
                padding: '7px 14px',
                borderRadius: 6,
                background: 'var(--bg-card, #161b22)',
                border: '1px solid var(--border-color, #30363d)',
                color: 'var(--text-secondary, #c9d1d9)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>

            <button
              onClick={handleApply}
              disabled={changesCount === 0}
              style={{
                padding: '7px 18px',
                borderRadius: 6,
                background: changesCount > 0 ? '#2563eb' : 'var(--bg-hover, #21262d)',
                border: '1px solid ' + (changesCount > 0 ? '#3b82f6' : '#30363d'),
                color: changesCount > 0 ? '#ffffff' : '#6b7280',
                fontSize: 12,
                fontWeight: 600,
                cursor: changesCount > 0 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: changesCount > 0 ? '0 2px 8px rgba(37, 99, 235, 0.4)' : 'none',
              }}
            >
              <Check size={14} />
              Применить распределение {changesCount > 0 ? `(${changesCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
